import type { OnInit } from '@angular/core';
import { Component, inject } from '@angular/core';
import type { MatDatepickerInputEvent } from '@angular/material/datepicker';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';

import type { ApiResponse } from '../../../models/api-response';
import type { Car } from '../../../models/car';
import type { FormModel, VehicleFilters } from '../../../models/car-filters';
import { ApiService } from '../../../services/api.service';
import { SharedModule } from '../../../shared/shared.module';
import { removeEmptyKeys } from '../../../shared/utitlities/validate-keys';
import { WebFacade } from '../../../store/web/web-facade.service';
import { WEB_CONFIG } from '../web-config';

type DateKeys = 'pickupDate' | 'dropoffDate';
type TimeKeys = 'pickupTime' | 'dropoffTime';
@Component({
  selector: 'app-car-filters',
  imports: [SharedModule],
  templateUrl: './car-filters.html',
  styleUrls: ['./car-filters.scss'],
})
export class CarFiltersComponent implements OnInit {
  dialogRef = inject(MatDialogRef<CarFiltersComponent>);
  data = inject(MAT_DIALOG_DATA);
  toast = inject(ToastrService);
  webfacadeService = inject(WebFacade);
  apiService = inject(ApiService);

  vehicleFilters: VehicleFilters = {
    name: null,
    make: null,
    transmission: null,
    seats: null,
    type: null,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    max_price: 0,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    min_price: 0,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    pickup_date: null,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    dropoff_date: null,
  };
  formModel: FormModel = {
    pickupDate: null,
    pickupTime: null,
    dropoffDate: null,
    dropoffTime: null,
  };
  today: Date = new Date();
  carId: number | null = null;
  isCarNotAvailable = false;

  ngOnInit(): void {
    this.carId = this.data.id;
  }

  onUpdateLocationDetails(): void {
    const FORM_VALUE = this.formModel;

    // Require all four fields
    if (!FORM_VALUE.pickupDate || !FORM_VALUE.pickupTime) {
      this.toast.error('Pickup date and time are required.');
      return;
    }
    if (!FORM_VALUE.dropoffDate || !FORM_VALUE.dropoffTime) {
      this.toast.error('Dropoff date and time are required.');
      return;
    }

    const UPDATED_FILTERS = {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      pickup_date: this.combineDateTime(
        FORM_VALUE.pickupDate as Date,
        FORM_VALUE.pickupTime as Date
      ),
      // eslint-disable-next-line @typescript-eslint/naming-convention
      dropoff_date: this.combineDateTime(
        FORM_VALUE.dropoffDate as Date,
        FORM_VALUE.dropoffTime as Date
      ),
    };

    const FILTERED_FILTERS = removeEmptyKeys(UPDATED_FILTERS);

    if (Object.keys(FILTERED_FILTERS).length > 0) {
      this.vehicleFilters.pickup_date = FILTERED_FILTERS.pickup_date!;
      this.vehicleFilters.dropoff_date = FILTERED_FILTERS.dropoff_date!;
      this.validateVehicle();
    }
  }

  combineDateTime(dateObj: Date | null, timeObj: Date | null): string {
    if (!dateObj || !timeObj) {
      return '';
    }
    const YEAR = dateObj.getFullYear();
    const MONTH = dateObj.getMonth(); // 0-based
    const DAY = dateObj.getDate();
    const HOURS = timeObj.getHours();
    const MINUTES = timeObj.getMinutes();
    const COMBINED_DATE = new Date(Date.UTC(YEAR, MONTH, DAY, HOURS, MINUTES));
    const IS_WITHOUT_MS = COMBINED_DATE.toISOString().replace(/\.\d{3}Z$/, 'Z');
    return IS_WITHOUT_MS;
  }

  destructureDateTimeForMaterial(isoString: string): { date: Date; time: Date } | null {
    if (!isoString) {
      return null;
    }

    const UTC_DATE = new Date(isoString);
    if (isNaN(UTC_DATE.getTime())) {
      return null;
    }

    const LOCAL_DATE = new Date(
      UTC_DATE.getUTCFullYear(),
      UTC_DATE.getUTCMonth(),
      UTC_DATE.getUTCDate()
    );

    const LOCAL_TIME = new Date();
    LOCAL_TIME.setHours(UTC_DATE.getUTCHours(), UTC_DATE.getUTCMinutes(), 0, 0);

    return { date: LOCAL_DATE, time: LOCAL_TIME };
  }

  onChange(ev: MatDatepickerInputEvent<Date>, controlName: DateKeys): void {
    const SELECTED_DATE = ev.value instanceof Date ? ev.value : new Date(ev.value ?? '');
    this.formModel[controlName] = SELECTED_DATE;

    if (controlName === 'dropoffDate') {
      this.formModel.dropoffTime = null;
    }

    if (controlName === 'pickupDate') {
      this.formModel.pickupTime = null;
      this.formModel.dropoffDate = null;
      this.formModel.dropoffTime = null;
    }
  }

  onTimeChange(value: Date | null, controlName: TimeKeys): void {
    this.formModel[controlName] = value || new Date();
  }

  clear(field: 'pickupTime' | 'dropoffTime' | 'dropoffDate' | 'pickupDate'): void {
    this.formModel[field] = null;
  }

  getCurrentTimeString(): string | null {
    const NOW = new Date();
    const PICKUP_DATE = this.formModel.pickupDate as Date;
    const DROPOFF_DATE = this.formModel.dropoffDate as Date;

    if (!PICKUP_DATE) {
      return null;
    }

    const IS_PICKUP_TODAY =
      PICKUP_DATE.getFullYear() === NOW.getFullYear() &&
      PICKUP_DATE.getMonth() === NOW.getMonth() &&
      PICKUP_DATE.getDate() === NOW.getDate();

    if (!IS_PICKUP_TODAY) {
      return null;
    } else if (!DROPOFF_DATE) {
      let hours = NOW.getHours();
      let minutes = NOW.getMinutes();

      // Round up to the next half hour
      if (minutes === 0) {
        // already on the hour
      } else if (minutes <= 30) {
        minutes = 30;
      } else {
        hours += 1;
        minutes = 0;
      }

      const HOURS_STR = hours.toString().padStart(2, '0');
      const MINUTES_STR = minutes.toString().padStart(2, '0');
      return `${HOURS_STR}:${MINUTES_STR}`;
    } else if (DROPOFF_DATE) {
      const IS_SAME_DATE =
        PICKUP_DATE.getFullYear() === DROPOFF_DATE.getFullYear() &&
        PICKUP_DATE.getMonth() === DROPOFF_DATE.getMonth() &&
        PICKUP_DATE.getDate() === DROPOFF_DATE.getDate();

      if (IS_SAME_DATE) {
        let hours = NOW.getHours();
        let minutes = NOW.getMinutes();

        // Round up to the next half hour
        if (minutes === 0) {
          // already on the hour
        } else if (minutes <= 30) {
          minutes = 30;
        } else {
          hours += 1;
          minutes = 0;
        }

        const HOURS_STR = hours.toString().padStart(2, '0');
        const MINUTES_STR = minutes.toString().padStart(2, '0');
        return `${HOURS_STR}:${MINUTES_STR}`;
      }
    }

    return null;
  }

  getDropoffMinTime(): string | null {
    const PICKUP_DATE = this.formModel.pickupDate as Date;
    const DROPOFF_DATE = this.formModel.dropoffDate as Date;
    const PICKUP_TIME = this.formModel.pickupTime as Date;

    if (!PICKUP_DATE || !DROPOFF_DATE || !PICKUP_TIME) {
      return null; // No restriction
    }

    // Check if same day
    const SAME_DAY =
      PICKUP_DATE.getFullYear() === DROPOFF_DATE.getFullYear() &&
      PICKUP_DATE.getMonth() === DROPOFF_DATE.getMonth() &&
      PICKUP_DATE.getDate() === DROPOFF_DATE.getDate();

    if (SAME_DAY) {
      // Return pickup time + 30 minutes
      let hours = PICKUP_TIME.getHours();
      let minutes = PICKUP_TIME.getMinutes();
      if (minutes < 30) {
        minutes = 30;
      } else {
        hours += 1;
        minutes = 0;
      }
      const HOUR_STR = hours.toString().padStart(2, '0');
      const MINUTES_STR = minutes.toString().padStart(2, '0');
      return `${HOUR_STR}:${MINUTES_STR}`;
    }

    // If dropoff is on a later date, allow any time
    return null;
  }

  onClose(): void {
    this.dialogRef.close({ key: false });
  }

  validateVehicle(): void {
    const FINAL_OBJ = {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      vehicle_id: this.carId,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      pickup_date: this.vehicleFilters.pickup_date,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      dropoff_date: this.vehicleFilters.dropoff_date,
    };
    this.apiService.post<Car>(WEB_CONFIG.validateCar, FINAL_OBJ).subscribe({
      next: (response: ApiResponse<Car>) => {
        if (response.statusCode === 200) {
          this.isCarNotAvailable = false;
          const LOCATION_DETAIL = {
            pickupDate: this.vehicleFilters.pickup_date,
            dropoffDate: this.vehicleFilters.dropoff_date,
          };
          this.dialogRef.close({ key: true, data: LOCATION_DETAIL });
        } else if (response.statusCode !== 200) {
          this.isCarNotAvailable = true;
        }
      },
      error: () => {
        this.isCarNotAvailable = true;
      },
    });
  }
}
